import { useRef, useEffect, useCallback, useState } from 'react';
import { autosaveNote } from '../services/notesService';

/**
 * useAutoSave — Debounced auto-save hook for research notes.
 *
 * Calls PATCH /api/notes/{id}/autosave after `delay` ms of idle.
 *
 * @param {string|null}  noteId  — Note ID (skip if null/new note)
 * @param {object}       content — Rich content JSON to save
 * @param {string}       title   — Note title
 * @param {object}       opts    — { delay: 3000, enabled: true }
 * @returns {{ saveStatus: 'idle'|'saving'|'saved'|'error', lastSaved: Date|null }}
 */
export default function useAutoSave(noteId, content, title, { delay = 3000, enabled = true } = {}) {
    const [saveStatus, setSaveStatus] = useState('idle');
    const [lastSaved, setLastSaved] = useState(null);
    const timerRef = useRef(null);
    const contentRef = useRef(content);
    const titleRef = useRef(title);

    // Keep refs current so the debounced callback always sees latest values
    useEffect(() => { contentRef.current = content; }, [content]);
    useEffect(() => { titleRef.current = title; }, [title]);

    const doSave = useCallback(async () => {
        if (!noteId || !enabled) return;
        try {
            setSaveStatus('saving');
            await autosaveNote(noteId, {
                content: contentRef.current,
                title: titleRef.current,
            });
            setSaveStatus('saved');
            setLastSaved(new Date());
        } catch (err) {
            console.error('[AutoSave] failed:', err);
            setSaveStatus('error');
        }
    }, [noteId, enabled]);

    // Debounce on content/title change
    useEffect(() => {
        if (!noteId || !enabled) return;
        // Mark unsaved immediately
        setSaveStatus('idle');

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(doSave, delay);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [content, title, noteId, delay, enabled, doSave]);

    // Flush on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                // Fire final save synchronously-ish
                if (noteId && enabled) {
                    autosaveNote(noteId, {
                        content: contentRef.current,
                        title: titleRef.current,
                    }).catch(() => { });
                }
            }
        };
    }, [noteId, enabled]);

    return { saveStatus, lastSaved };
}
